/**
 * Embeddings playground composable
 * Manages embeddings generation state and API calls
 */

import { ref, computed } from 'vue';
import { useNotification } from '@asabdev/asab-ui';
import { useApiError } from '@/features/auth/composables/useApiError';
import { useAuthStore } from '@/stores/auth';
import { useSessionStorage } from './useSessionStorage';
import { createOpenAIClient } from '../lib/openai-client';

export function useEmbeddingsPlayground() {
  const authStore = useAuthStore();
  const { clearError, safeApiCall } = useApiError();
  const { showSuccess, showError } = useNotification();
  const { session, loadSession, saveSession, createEmptySession } = useSessionStorage();

  if (!session.value) {
    const loaded = loadSession();
    if (!loaded) {
      session.value = createEmptySession();
    }
  }

  const inputText = ref<string>(session.value?.embeddings.inputText || '');
  const selectedModel = ref<string>(session.value?.embeddings.selectedModel || '');
  const embeddings = ref<number[] | null>(session.value?.embeddings.embeddings || null);
  const isLoading = ref(false);

  // Optional; only honored by models that support dim reduction.
  const dimensions = ref<number | null>(session.value?.embeddings.dimensions ?? null);
  // Optional; Gemini-only. Serialized only when non-empty.
  const taskType = ref<string>('');

  const generateEmbeddings = async (text: string): Promise<void> => {
    if (!authStore.token || !selectedModel.value) return;

    isLoading.value = true;
    clearError();

    const result = await safeApiCall(
      async () => {
        const client = createOpenAIClient({
          token: authStore.token!,
          isApiKey: false,
        });

        const params: Record<string, unknown> = {
          model: selectedModel.value,
          input: text,
          // Always float on the playground — base64 is only useful for
          // bandwidth-constrained server-to-server pipelines.
          encoding_format: 'float',
        };
        if (dimensions.value && dimensions.value > 0) params.dimensions = dimensions.value;
        if (taskType.value.trim()) params.task_type = taskType.value.trim();
        // Pass the authenticated user's ID for provider-side abuse tracking.
        if (authStore.user?.id) params.user = authStore.user.id;

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const response = await client.embeddings.create(params as any);

        embeddings.value = response.data[0].embedding as number[];
        return true;
      },
      {
        context: 'Generate Embeddings',
        retryOptions: { maxRetries: 2, initialDelay: 1000 },
      }
    );

    isLoading.value = false;

    if (result) {
      showSuccess('Embeddings generated successfully');
      updateSession();
    } else {
      showError('Failed to generate embeddings');
    }
  };

  const setModel = (modelId: string): void => {
    selectedModel.value = modelId;
    updateSession();
  };

  const setInputText = (text: string): void => {
    inputText.value = text;
    updateSession();
  };

  const updateSession = (): void => {
    if (session.value) {
      session.value.embeddings = {
        inputText: inputText.value,
        selectedModel: selectedModel.value,
        embeddings: embeddings.value,
        dimensions: dimensions.value,
      };
      session.value.timestamp = new Date();
      saveSession(session.value);
    }
  };

  return {
    // Raw refs — template uses v-model so they must be writable.
    inputText,
    selectedModel,
    dimensions,
    taskType,
    embeddings: computed(() => embeddings.value),
    isLoading: computed(() => isLoading.value),
    generateEmbeddings,
    setModel,
    setInputText,
  };
}
